import { Box, Typography } from "@mui/material";
import clsx from "clsx";
import { observer } from "mobx-react-lite";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import DeleteIcon from "../../../../assets/images/icons/DeleteIcon";
import DownloadIcon from "../../../../assets/images/icons/DownloadIcon";
import FullscreenIcon from "../../../../assets/images/icons/Image/FullscreenIcon.tsx";
import BriaTooltip from "../../../../components/common/BriaTooltip/BriaTooltip";
import CopyToClipboardButton from "../../../../components/common/CopyToClipboardButton/CopyToClipboardButton.tsx";
import FullScreenViewer from "../../../../components/common/FullScreenViewer/FullScreenViewer";
import { FullScreenImageType } from "../../../../components/common/FullScreenViewer/Image/FullScreenImage";
import LoadingAnimation from "../../../../components/common/LoadingAnimation/LoadingAnimation";
import { APPS } from "../../../../constants/AppsConstants.ts";
import RouterConstants from "../../../../constants/RouterConstants.ts";
import { fileDownload } from "../../../../helpers/files";
import { useAppStore } from "../../../../hooks/useStores";
import { PlaygroundImage, PlaygroundResult } from "../../../../models/playground";
import { TextToImageConfigType } from "../../../../models/text-to-image.ts";
import iframeStore from "../../../IframeNew/iframe-store.tsx";
import PlaygroundVideo from "../Video/PlaygroundVideo";
import { TgVideoGenerationConfigType } from "../video-generation.ts";
import styles from "./VideoGenerationResult.module.scss";

type Props = {
	playgroundResult: PlaygroundResult;
};

const AVG_RESULT_DURATION = 240; // Video generation typically takes longer than images

const VideoGenerationResult = ({ playgroundResult }: Props) => {
	const { t } = useTranslation("translation", { keyPrefix: "playground.videoGeneration.result" });
	const config = playgroundResult.config as TgVideoGenerationConfigType;
	const { playgroundStore, videoGenerationStore } = useAppStore();
	// Find the original image that was used to generate this video
	const originalImage = playgroundStore.playgroundResults
		.flatMap((result) => result.images)
		.find((image) => image.url === config.image);

	const imageConfig = (originalImage?.config || playgroundResult.config) as TextToImageConfigType;
	const loading = !playgroundResult.video?.src;
	const loadingDuration = AVG_RESULT_DURATION * 1.1;
	const [isViewerOpen, setIsViewerOpen] = useState(false);
	const isTgEvaluation = location.pathname.includes(RouterConstants.NEW_TG.MODELS.EVALUATION.path);

	const handleDownloadVideo = () => {
		if (playgroundResult.video?.src) {
			const fileName = `video_generation_${config.model_id}_${new Date()
				.toISOString()
				.replace("T", "_")
				.replace(/:/g, "-")
				.substring(0, 19)}`;
			fileDownload(playgroundResult.video.src, fileName, "mp4");
		}
	};

	// Create a PlaygroundImage-like object for the video thumbnail
	const videoImage: PlaygroundImage = {
		id: playgroundResult.id,
		url: config.image || "",
		config: config,
		type: playgroundResult.type === APPS.VIDEO_GENERATION ? APPS.VIDEO_GENERATION : undefined,
	};

	return (
		<Box className={clsx(styles.videoGalleryWrapper, "galleryWrapper")}>
			<Box className={styles.header}>
				<Box className={styles.topHeader}>
					<Typography className={styles.labelModelName}>
						{imageConfig.model_name}
						{isTgEvaluation && (
							<Box className={styles.badgesContainer}>
								<Box component="span" className={styles.badge}>
									{imageConfig.training_version}
								</Box>
								{imageConfig.checkpoint_number !== "default" && (
									<Box component="span" className={styles.badge}>
										{imageConfig.checkpoint_number}
									</Box>
								)}
							</Box>
						)}
					</Typography>
					{loading ? (
						<LoadingAnimation loading={loading} progressBarTime={loadingDuration} showAnimation={false} />
					) : (
						<Box className={styles.actionBtns}>
							<CopyToClipboardButton
								className={styles.iconButton}
								textToCopy={
									imageConfig?.generation_prefix && imageConfig?.training_version !== "fibo"
										? `${imageConfig?.generation_prefix}${imageConfig?.prompt}`
										: imageConfig?.prompt
								}
								tooltipText={t("copyPrompt")}
							/>
							{(!iframeStore.isIframe() ||
								iframeStore.iframe.config.general_config?.enable_download ||
								iframeStore.iframe.config.general_config?.enable_save) && (
								<BriaTooltip title={t("download")}>
									<DownloadIcon className={styles.iconButton} onClick={handleDownloadVideo} />
								</BriaTooltip>
							)}
							<BriaTooltip title={t("delete")}>
								<DeleteIcon
									className={styles.iconButton}
									onClick={() => playgroundStore.deleteResult(playgroundResult.id)}
								/>
							</BriaTooltip>
							<BriaTooltip title={t("fullScreen")}>
								<FullscreenIcon className={styles.iconButton} onClick={() => setIsViewerOpen(true)} />
							</BriaTooltip>
						</Box>
					)}
				</Box>
				<Box className={styles.headerText}>
					{imageConfig?.generation_prefix &&
					!imageConfig.restyle_portrait &&
					imageConfig?.training_version !== "fibo" ? (
						<>
							<span className={styles.generationPrefix}>{imageConfig?.generation_prefix}</span>
							{imageConfig?.prompt}
						</>
					) : (
						imageConfig?.prompt
					)}
				</Box>
			</Box>
			<Box className={styles.gallery}>
				{playgroundResult.video && (
					<PlaygroundVideo
						src={playgroundResult.video.src}
						thumbnail={config.image}
						aspectRatio={imageConfig?.aspect_ratio}
						className={styles.video}
						loading={loading}
						onTryAgain={() => {
							videoGenerationStore.tryAgainTgImageToVideo(videoImage, playgroundResult.id);
						}}
						asyncProps={playgroundResult.video?.asyncProps}
					/>
				)}
			</Box>
			{isViewerOpen && playgroundResult.video && (
				<FullScreenViewer
					open={isViewerOpen}
					images={[{ src: playgroundResult.video.src || "", type: "video" }] as FullScreenImageType[]}
					currentIndex={0}
					displayMode="arrows"
					onClose={() => setIsViewerOpen(false)}
					onDownload={() => handleDownloadVideo()}
					hideDownloadIcon={false}
					hideDeleteIcon={true}
					hideFavoriteIcon={true}
				/>
			)}
		</Box>
	);
};

export default observer(VideoGenerationResult);
